import { createContext, useContext, useMemo, type ReactNode } from "react"
import { useThreadContext } from "@/providers/ThreadProvider"
import { useRewriteStream, type Decision } from "../hooks/useRewriteStream"
import type { RewriteAgentState } from "../hooks/useOIORewriteAgent.types"

import { useTabOwnership } from "../../agentSessions/useTabOwnership"

export type RewriteContextValue = RewriteAgentState & {
  run: (prompt?: string) => Promise<unknown>
  reattach: () => Promise<void>
  running: boolean
  resume: (decisions: Decision[]) => Promise<void>
  currentRunId: string | null
  error: string | null
}

// Exported so mock replay (src/features/oio/mock/MockOIORewriteProvider) can
// provide an inert stub value; real consumers keep using the provider below.
export const OIORewriteContext = createContext<RewriteContextValue | null>(null)

export function OIORewriteProvider({ children }: { children: ReactNode }) {
  const { activeThread } = useThreadContext()
  const { currentTabId } = useTabOwnership()
  const handle = useRewriteStream({ threadId: activeThread?.id, currentTabId })

  // Memoized so every OIORewriteContext consumer (OIOWorkspace, etc.) doesn't
  // re-render on every OIORewriteProvider render — only when something the
  // context value actually carries has changed. handle.state/run/reattach/
  // resume are themselves stable references from useRewriteStream (useState
  // + useCallback) unless their own underlying data changed, so this
  // correctly skips re-creation on unrelated renders.
  const value: RewriteContextValue = useMemo(
    () => ({
      ...handle.state,
      run: handle.run,
      reattach: handle.reattach,
      running: handle.running,
      resume: handle.resume,
      currentRunId: handle.currentRunId,
      error: handle.error,
    }),
    [handle.state, handle.run, handle.reattach, handle.running, handle.resume, handle.currentRunId, handle.error],
  )

  return (
    <OIORewriteContext.Provider value={value}>
      {children}
    </OIORewriteContext.Provider>
  )
}

export function useOIORewriteContext(): RewriteContextValue {
  const value = useContext(OIORewriteContext)
  if (!value) {
    throw new Error("useOIORewriteAgent must be used inside OIORewriteProvider")
  }
  return value
}
